import React from 'react';
import { atom, isShallowEqual } from '@reatom/framework';
import { useAtom, useCtx } from '@reatom/npm-react';

import type { Observable } from '@/js/libs/observable/index.ts';
import { isObservable } from '@/js/libs/observable/index.ts';

type ObservableState<T> = T extends Observable<infer State> ? State : T;

type CreateContexts = {
  <
    T extends Record<
      string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (parameter?: any) => Record<string, any>
    >,
  >(
    contextsHook: T,
  ): {
    // @ts-expect-error bad type inference
    [K in keyof T as `use${K}Context`]: {
      (): ReturnType<T[K]>;
      <Prop extends keyof ReturnType<T[K]>>(prop: Prop): ObservableState<ReturnType<T[K]>[Prop]>;
      <Selected>(selector: (state: ReturnType<T[K]>) => Selected): ObservableState<Selected>;
      Provider: React.FC<React.PropsWithChildren<Parameters<T[K]> extends [infer Parameter] ? { value: Parameter } : { value?: never }>>;
    };
  };
};

const NEVER_VALUE = {};

// @ts-expect-error generic mismatch
export const createContexts: CreateContexts = (contextsHook) => {
  const result: Record<string, () => unknown> = {};

  for (const [name, useContextLogic] of Object.entries(contextsHook)) {
    const hookName = `use${name}Context`;
    const providerName = `${name}Provider`;
    const context = React.createContext(NEVER_VALUE as ReturnType<typeof useContextLogic>);
    const contextAtom = atom(NEVER_VALUE as ReturnType<typeof useContextLogic>, `${name}Context`);
    const Provider = ({ children, value: parameter }: React.PropsWithChildren<{ value?: unknown }>): React.JSX.Element => {
      const state = useContextLogic(parameter);
      const value = React.useMemo(
        () => state,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        Object.values(state),
      );
      const ctx = useCtx();
      if (!isShallowEqual(ctx.get(contextAtom), value)) {
        contextAtom(ctx, value);
      }
      return React.createElement(context.Provider, {
        value,
        children,
      });
    };
    Provider.displayName = providerName;

    result[hookName] = Object.assign(
      (selector?: string | ((state: ReturnType<typeof useContextLogic>) => unknown)) => {
        if (selector) {
          return useAtom(
            (ctx) => {
              const state = ctx.spy(contextAtom);
              const value = typeof selector === 'function' ? selector(state) : state[selector];
              return isObservable(value) ? ctx.spy(value.__INTERNAL_DO_NOT_USE_ATOM__) : value;
            },
            [],
            { name: `${name}.useAtom._selector` },
          )[0];
        }
        const value = React.useContext(context);
        if (!value || value === NEVER_VALUE) {
          throw new Error(`${hookName} must be used within a ${providerName}`);
        }
        return value;
      },
      {
        Provider,
      },
    );
  }

  return result;
};
